<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import TextInput from 'next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import NextButton from 'next/button/Button.vue';

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({}),
  },
  customRoles: {
    type: Array,
    default: () => [],
  },
  showCustomRoles: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue']);
const { t } = useI18n();

const parseMappings = mappings =>
  Object.entries(mappings || {}).map(([group, mapping]) => ({
    group,
    role:
      mapping.role === 1 ||
      mapping.role === '1' ||
      mapping.role === 'administrator'
        ? 'administrator'
        : 'agent',
    customRoleId: mapping.custom_role_id || '',
  }));

const serializeMappings = value => {
  const mappings = {};
  value.forEach(row => {
    const group = row.group?.trim();
    if (!group) return;

    const mapping = {
      role: row.role === 'administrator' ? 'administrator' : 'agent',
    };
    if (props.showCustomRoles && row.customRoleId) {
      mapping.custom_role_id = Number(row.customRoleId);
    }
    mappings[group] = mapping;
  });
  return mappings;
};

const rows = ref(parseMappings(props.modelValue));

watch(
  () => props.modelValue,
  value => {
    const serialized = serializeMappings(rows.value);
    if (JSON.stringify(value || {}) === JSON.stringify(serialized)) return;
    rows.value = parseMappings(value);
  }
);

const roleOptions = computed(() => [
  { value: 'agent', label: t('SECURITY_SETTINGS.SAML.ROLE_MAPPINGS.AGENT') },
  {
    value: 'administrator',
    label: t('SECURITY_SETTINGS.SAML.ROLE_MAPPINGS.ADMINISTRATOR'),
  },
]);

const customRoleOptions = computed(() => [
  {
    value: '',
    label: t('SECURITY_SETTINGS.SAML.ROLE_MAPPINGS.CUSTOM_ROLE_NONE'),
  },
  ...props.customRoles.map(role => ({ value: role.id, label: role.name })),
]);

const commit = () => {
  emit('update:modelValue', serializeMappings(rows.value));
};

const updateRow = (index, patch) => {
  rows.value[index] = { ...rows.value[index], ...patch };
  commit();
};

const addRow = () => {
  rows.value = [...rows.value, { group: '', role: 'agent', customRoleId: '' }];
};

const removeRow = index => {
  rows.value = rows.value.filter((_, rowIndex) => rowIndex !== index);
  commit();
};
</script>

<template>
  <section class="grid gap-3">
    <div>
      <h4 class="text-sm font-medium text-n-slate-12">
        {{ t('SECURITY_SETTINGS.SAML.ROLE_MAPPINGS.TITLE') }}
      </h4>
      <p class="text-sm text-n-slate-11 mt-1">
        {{ t('SECURITY_SETTINGS.SAML.ROLE_MAPPINGS.DESCRIPTION') }}
      </p>
    </div>

    <div
      v-for="(row, index) in rows"
      :key="index"
      class="grid gap-3 md:grid-cols-[1fr_10rem_minmax(0,12rem)_auto] items-end"
    >
      <TextInput
        :model-value="row.group"
        class="w-full"
        :placeholder="
          t('SECURITY_SETTINGS.SAML.ROLE_MAPPINGS.GROUP_PLACEHOLDER')
        "
        :aria-label="t('SECURITY_SETTINGS.SAML.ROLE_MAPPINGS.GROUP_LABEL')"
        @update:model-value="updateRow(index, { group: $event })"
      />
      <Select
        :model-value="row.role"
        class="w-full"
        :options="roleOptions"
        :aria-label="t('SECURITY_SETTINGS.SAML.ROLE_MAPPINGS.ROLE_LABEL')"
        @update:model-value="updateRow(index, { role: $event })"
      />
      <Select
        v-if="showCustomRoles"
        :model-value="row.customRoleId"
        class="w-full"
        :options="customRoleOptions"
        :aria-label="
          t('SECURITY_SETTINGS.SAML.ROLE_MAPPINGS.CUSTOM_ROLE_LABEL')
        "
        @update:model-value="updateRow(index, { customRoleId: $event })"
      />
      <NextButton
        type="button"
        ghost
        sm
        slate
        icon="i-lucide-trash-2"
        :label="t('SECURITY_SETTINGS.SAML.ROLE_MAPPINGS.REMOVE')"
        @click="removeRow(index)"
      />
    </div>

    <NextButton
      type="button"
      faded
      sm
      :label="t('SECURITY_SETTINGS.SAML.ROLE_MAPPINGS.ADD')"
      @click="addRow"
    />
  </section>
</template>
